import { useCallback, useRef, useState } from 'react';

import type { LeadCardContactMethodData } from '@app/components/LeadCard/data';
import { useLeadClientEditing } from '@app/components/LeadCard/model/useLeadClientEditing';
import type { LeadClientPatch } from '@app/components/LeadCard/model/useLeadClientEditing';
import type { PhoneState } from '@app/components/Phone/types';
import { selectActiveBranchCountryId, useAppSelector } from '@app/store';

const MAIN_PHONE_ID = 'phone-1';
const EXTRA_PHONE_ID = 'phone-2';

const getPhoneDigits = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '');

// Only real client phones are editable: masked numbers (no right to see
// contacts) and messenger contacts are not.
export const isEditablePhoneItem = ({
  id,
  kind,
  value,
}: LeadCardContactMethodData): boolean =>
  kind === 'phone' &&
  (id === MAIN_PHONE_ID || id === EXTRA_PHONE_ID) &&
  !value.includes('*');

export const useUserContactMethods = () => {
  const { canEditClient, isSaving, fetchClient, saveClientPatch } =
    useLeadClientEditing();
  const activeBranchCountryId = useAppSelector(selectActiveBranchCountryId);

  const [editingPhoneId, setEditingPhoneId] = useState<string | null>(null);
  const [editingPhone, setEditingPhone] = useState<PhoneState | null>(null);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);
  const editingPhoneIdRef = useRef<string | null>(null);

  const cancelPhoneEditHandler = useCallback(() => {
    editingPhoneIdRef.current = null;
    setEditingPhoneId(null);
    setEditingPhone(null);
    setIsPhoneLoading(false);
  }, []);

  // The editor needs the country of the stored number, which the lead card
  // view does not carry, so the client is loaded when editing starts.
  const startPhoneEditHandler = useCallback(
    async (itemId: string) => {
      editingPhoneIdRef.current = itemId;
      setEditingPhoneId(itemId);
      setEditingPhone(null);
      setIsPhoneLoading(true);

      const client = await fetchClient();

      if (editingPhoneIdRef.current !== itemId) return;

      setIsPhoneLoading(false);

      if (!client) {
        cancelPhoneEditHandler();
        return;
      }

      const isMainPhone = itemId === MAIN_PHONE_ID;

      setEditingPhone({
        Phone: getPhoneDigits(isMainPhone ? client.Phone1 : client.Phone2),
        CountryId:
          (isMainPhone ? client.Phone1CountryId : client.Phone2CountryId) ??
          activeBranchCountryId ??
          1,
      });
    },
    [activeBranchCountryId, cancelPhoneEditHandler, fetchClient]
  );

  const savePhoneHandler = useCallback(
    async (phone: PhoneState) => {
      if (!editingPhoneId || !editingPhone) return;

      const digits = getPhoneDigits(phone.Phone);

      if (
        digits === editingPhone.Phone &&
        phone.CountryId === editingPhone.CountryId
      ) {
        cancelPhoneEditHandler();
        return;
      }

      // A changed main number is no longer the verified one.
      const patch: LeadClientPatch =
        editingPhoneId === MAIN_PHONE_ID
          ? {
              Phone1: digits,
              Phone1CountryId: phone.CountryId,
              ...(digits !== editingPhone.Phone && { IsPhoneVerified: false }),
            }
          : {
              Phone2: digits || null,
              Phone2CountryId: digits ? phone.CountryId : null,
            };

      const isSaved = await saveClientPatch(patch, 'Телефон клиента сохранён');

      if (isSaved) cancelPhoneEditHandler();
    },
    [cancelPhoneEditHandler, editingPhone, editingPhoneId, saveClientPatch]
  );

  return {
    canEditPhones: canEditClient,
    mainPhoneId: MAIN_PHONE_ID,
    editingPhoneId,
    editingPhone,
    isPhoneLoading,
    isPhoneSaving: isSaving,
    startPhoneEditHandler,
    cancelPhoneEditHandler,
    savePhoneHandler,
  };
};
